import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { ElectricalSlots } from "building-types/electrical/electricalSlots";
import { ControllerType, LoadType } from "building-types/electrical/electricalTypesEnum";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { ExteriorPropSettings } from "building-types/settings/exteriorPropSettings";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { RearPorchSettings } from "building-types/settings/rearPorchSettings";
import { EaveBracketSide } from "building-types/settings/roofBaseTypes";
import { SettingsType } from "building-types/settings/settingsType";
import {
  ColonnadeTerminationType,
  WallIntersectionType,
  EaveType,
  FasciaTopTrimSize,
  ShutterType,
  ApronCasingType,
  SideCasingType,
  StoolCasingType,
  WindowMullionType,
} from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { headCasingTrimNew } from "../settingsTools";
import { defaultElectricalSettings } from "./builtinElectricalSettings";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultAwningSettings,
  defaultExteriorWindowCasingSettings,
  defaultFriezeBoardSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultWainscotingSettings,
} from "./defaultSettings";
import { defaultPorchViewNotes } from "./defaultViewNoteSettings";

const defaultElliotExteriorPropSettings: ExteriorPropSettings = {
  type: SettingsType.EXTERIOR_PROP_SETTINGS,
  propTypes: {
    ...defaultExteriorPropCatalog,
    garageDoorSingle: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
      propAssetId: PropId.DoorGar_S8x7_2x4Pattern_H_01,
    },
    garageDoorDouble: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
      propAssetId: PropId.DoorGar_D16x7_4x4PatternHLines_01,
    },
    doorSingleGlass: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
      propAssetId: PropId.DoorExt_Single36_QuarterSolid4_02,
    },
    doorSingleGlassLow: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
      propAssetId: PropId.DoorExt_Single36_QuarterSolid4_01,
    },
    garageDoorDoubleSecondary: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLESECONDARY],
      propAssetId: PropId.DoorGar_D16x7_4x3Pattern_2Lite4th_H_01,
    },
  },
};

const wainscotingHeight = 23.99;

type WindowSettings = Pick<HomeSettings, "windowMullions" | "windowShutters" | "exteriorWindowCasing">;

const defaultElliottWindowSettings: WindowSettings = {
  exteriorWindowCasing: {
    ...defaultExteriorWindowCasingSettings,
    stoolCasingType: StoolCasingType.DISABLED,
    stoolCasingDepth: 2,
    apronCasingType: ApronCasingType.TRIM,
    apronHeight: 3.5,
    sideCasingType: SideCasingType.TRIM,
    sideCasingWidth: 3.5,
    stoolCasingThickness: 1.5,
    ...headCasingTrimNew(),
    casingOrientation: StreetOrientationMode.FRONT_FACES,
  },
  windowMullions: {
    type: SettingsType.WINDOW_MULLION_SETTINGS,
    mullionType: WindowMullionType.ORTHOGONAL,
    paneOneXCount: 2,
    paneOneYCount: 2,
    paneTwoYCount: 0,
    paneTwoXCount: 2,
    verticalThickness: 1,
    horizontalThickness: 1,
    horizontalSplit: false,
  },
  windowShutters: {
    type: SettingsType.WINDOW_SHUTTER_SETTINGS,
    shutterType: ShutterType.DISABLED,
  },
};

const wainscotingThickness = 2.5;

const frenchCountryPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 90,
  columnId: "DEFAULT",
  doorPathColumnSpread: 60,
  columnBracketPropId: BuiltInExteriorPropId.BRACKETFRENCHCOUNTRY,
  colonnadeTerminationRules: [
    {
      endType: WallIntersectionType.TEE,
      terminationType: ColonnadeTerminationType.COLUMN,
    },
  ],
  includeRailing: false,
  baseColumnOffset: true,
};

const southernPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 90,
  columnId: "DEFAULT",
  doorPathColumnSpread: 60,
  includeRailing: false,
  baseColumnOffset: true,
};

const rearPorchSettings: RearPorchSettings = {
  type: SettingsType.REAR_PORCH_SETTINGS,
  maxColumnSpacing: 240,
  columnId: "DEFAULT",
  doorPathColumnSpread: 60,
  colonnadeTerminationRules: [
    {
      endType: WallIntersectionType.TEE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
  ],
  includeRailing: false,
  baseColumnOffset: true,
  internalColumnStructureSize: 3.5,
  internalHeaderStructureWidth: 3.5,
  headerCasingOffsetFromCenterLine: 1.375,
  headerCasingThickness: 8.25,
  columnCasingOffset: 0,
  viewNotes: defaultPorchViewNotes,
};

const elliottElectricalSlots: ElectricalSlots = {
  controllers: {
    primary: ControllerType.SWITCH,
    sensor: ControllerType.SENSOR,
  },
  loads: {
    primaryLight: LoadType.RECESSED_CEILING_LIGHT,
    secondaryLight: LoadType.MOUNTED_CEILING_LIGHT,

    outlet: LoadType.OUTLET,
    outletGFCI: LoadType.OUTLET_GFCI,

    recessedCeilingLight: LoadType.RECESSED_CEILING_LIGHT,

    pendantCeilingLight: LoadType.PENDANT_CEILING_LIGHT,

    keylessCeilingLight: LoadType.KEYLESS_CEILING_LIGHT,

    flourescentCeilingLight: LoadType.FLOURESCENT_CEILING_LIGHT,

    trackCeilingLight: LoadType.TRACK_CEILING_LIGHT,

    mountedWallLight: LoadType.MOUNTED_WALL_LIGHT,

    fanCeiling: LoadType.CEILING_FAN,

    smokeDetector: LoadType.SMOKE_DETECTOR,

    carbonMonoxideDetector: LoadType.CARBON_MONOXIDE_DETECTOR,

    thermostat: LoadType.THERMOSTAT,

    bathFan: LoadType.BATH_FAN,

    cableOutlet: LoadType.CABLE_OUTLET,

    floodLight: LoadType.FLOOD_LIGHT,

    outletGFCIwaterProof: LoadType.OUTLET_GFCI_WP,

    outletFloor: LoadType.OUTLET_FLOOR,
    outlet220V: LoadType.OUTLET_220V,
    outletCAT5: LoadType.OUTLET_CAT5,

    garageDoorOpener: LoadType.GARAGE_DOOR_OPENER,

    dishDisposal: LoadType.DISH_DISPOSAL,

    doorbellChime: LoadType.DOORBELL_CHIME,

    outlet240V: LoadType.OUTLET_240V,

    outletHDMI: LoadType.OUTLET_HDMI,

    hoseBib: LoadType.HOSE_BIB,
  },
};

const elliottSettings: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.Elliott_H00]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Elliott_H00],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Elliott_H00 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Elliott_H00],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_2x4Pattern_H_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_2x4Pattern_H_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_01,
        },
      },
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 12,
      eaveType: EaveType.BOXED_BOX,
      eaveReturnDistance: 6,
      eaveExtensionAtCoplanarRake: 6,
      eaveExtensionAtNonCoplanarRake: 0,
      eaveExtensionAtCoplanarWall: 1.75,
      rakeExtensionAtCoplanarWall: 0,
      rakeBracketPropId: BuiltInExteriorPropId.RAKEBRACKET,
      rakeBracketParams: [48],
      rakeBracketAtPeak: true,
      eaveBracketParamsNormalized: true,
      eaveBracketParams: [0.5],
      eaveBracketSide: EaveBracketSide.BOTH,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
      roofFramingSpacing: 24,
      lookoutRafterSpacing: 24,
      includeFlyRafters: true,
      includeLookoutRafters: true,
      includeStickFramingAtStart: true,
      webChordSpacingAtStart: 16,
      includeStickFramingAtEnd: true,
      webChordSpacingAtEnd: 16,
      setBackForStepDownTruss: 72,
      generateRoofFraming: false,
    },
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 6,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: wainscotingHeight,
      thickness: wainscotingThickness,
      wainscotingMode: StreetOrientationMode.FRONT_AND_SIDE_FACES,
    },
    porch: southernPorchSettings,
    rearPorch: rearPorchSettings,

    ...defaultElliottWindowSettings,

    windowShutters: {
      type: SettingsType.WINDOW_SHUTTER_SETTINGS,
      shutterType: ShutterType.DISABLED,
    },

    electrical: { ...defaultElectricalSettings, slots: elliottElectricalSlots },
  },
  [BuiltinHomeSettingsId.Elliott_H10]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Elliott_H10],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Elliott_H10 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Elliott_H10],
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 14,
      eaveType: EaveType.BOXED_BOX,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: wainscotingHeight,
      thickness: wainscotingThickness,
      wainscotingMode: StreetOrientationMode.FRONT_AND_SIDE_FACES,
    },

    ...defaultElliottWindowSettings,

    awning: defaultAwningSettings,
    porch: southernPorchSettings,
    rearPorch: rearPorchSettings,

    exteriorProps: defaultElliotExteriorPropSettings,
    electrical: { ...defaultElectricalSettings, slots: elliottElectricalSlots },
  },
  [BuiltinHomeSettingsId.Elliott_H20]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Elliott_H20],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Elliott_H20 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Elliott_H20],
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 15,
      eaveType: EaveType.BOXED_BOX,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: wainscotingHeight,
      thickness: wainscotingThickness,
      wainscotingMode: StreetOrientationMode.FRONT_AND_SIDE_FACES,
    },

    ...defaultElliottWindowSettings,

    awning: defaultAwningSettings,
    porch: frenchCountryPorchSettings,
    rearPorch: rearPorchSettings,
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_2x4Pattern_H_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_2x4Pattern_H_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_01,
        },
      },
    },
    electrical: { ...defaultElectricalSettings, slots: elliottElectricalSlots },
  },
  [BuiltinHomeSettingsId.Elliott_J00]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Elliott_J00],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Elliott_J00 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Elliott_J00],
    },
    electrical: { ...defaultElectricalSettings, slots: elliottElectricalSlots },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 12,
      eaveType: EaveType.BOXED_BOX,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 20,
      thickness: wainscotingThickness,
      wainscotingMode: StreetOrientationMode.FRONT_AND_SIDE_FACES,
    },

    ...defaultElliottWindowSettings,

    awning: defaultAwningSettings,
    porch: {
      ...southernPorchSettings,
      includeRailing: false,
      multiColumn: false,
      multiColumnFrontOnly: false,
      multiColumnSpread: 16,
    },
    rearPorch: rearPorchSettings,
    exteriorProps: defaultElliotExteriorPropSettings,
  },
  [BuiltinHomeSettingsId.Elliott_F00]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Elliott_F00],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Elliott_F00 + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Elliott_F00],
    },
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 12,
      rakeOverhang: 12,
      shedOverhang: 12,
      eaveType: EaveType.BOXED_BOX,
      friezeBoardOrientation: StreetOrientationMode.FRONT_FACES,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 20,
      thickness: wainscotingThickness,
      wainscotingMode: StreetOrientationMode.FRONT_AND_SIDE_FACES,
    },
    ...defaultElliottWindowSettings,
    awning: defaultAwningSettings,
    porch: {
      ...southernPorchSettings,
      includeRailing: true,
    },
    rearPorch: rearPorchSettings,
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_2x4Pattern_H_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_2x4Pattern_H_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_QuarterSolid4_01,
        },
      },
    },
    electrical: { ...defaultElectricalSettings, slots: elliottElectricalSlots },
  },
};

export default elliottSettings;
